import React from 'react';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import Collapse from '@material-ui/core/Collapse';
import IconButton from '@material-ui/core/IconButton';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import Tooltip from '@material-ui/core/Tooltip';
import {
    formatAgreementTypeName,
    getOwnersStringForAgreementGroupByType,
} from '../HubUtils';
import ExternalTable from './ExternalTable';

const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
});

/**
 * This method calculates a dedicated total for non-RAS accounts for a single agreementGroupByType.
 * Note: this handles any partial shares, etc. by utilizing the dedicatedAmount property from the service
 * which manges the calculation of dedicatedTotal
 * @param {object} agreementGroupByType A single agreementGroupByType
 * @returns {number} A dedicated total of non-RAS internal agreements in this single agreementGroupByType coming into the RAS program
 */
const calculateNonRasAgreementGroupByTypeTotal = (agreementGroupByType) =>
    agreementGroupByType.agreements
        .map(agr => agr.holdings)
        .flat()
        .reduce((total, holding) => total + holding.dedicatedAmount, 0);

/**
 * This method calculates a dedicated total for non-RAS accounts for multiple agreementsGroupByType.
 * Note: this handles any partial shares, etc. by utilizing the dedicatedAmount property from the service
 * which manges the calculation of dedicatedTotal
 * @param {array} agreementGroupByType A list of agreementGroupByType
 * @returns {number} A dedicated total of non-RAS internal agreements in all agreementsGroupByType coming into the RAS program
 */
const calculateNonRasAgreementGroupByTypeArrTotal = (agreementsGroupByType) =>
    agreementsGroupByType
        .reduce((total, agreementGroupByType) =>
            total + calculateNonRasAgreementGroupByTypeTotal(agreementGroupByType), 0);

/**
 * This method calculates a dedicated total for external accounts coming into the RAS program. Filtering of unselected external accounts
 * is handled.
 * @param {array} externalAccounts A list of externalAccount
 * @returns {number} A dedicated total of external accounts coming into the RAS program
 */
const calculateExternalAccountsTotal = (externalAccounts) =>
    externalAccounts
        .filter(externalAccount => externalAccount.isSelected)
        .reduce((total, externalAccount) => total + externalAccount.amount, 0);

const determineFundName = (agr) => {
    if (agr.productTypeId === 20) {
        return "Brokerage";
    }
    if (agr.productTypeId === 110) {
        return "Active Plus Portfolio";
    }
    return agr.holdings[0].fundName;
};

const determineFundTicker = (agr) =>
    (agr.productTypeId === 20 || agr.productTypeId === 110) ? "" : agr.holdings[0].ticker;

const TRPTooltip = withStyles(() => ({
    tooltip: {
        backgroundColor: '#1E2D39',
        color: 'rgba(255, 255, 255, 0.95)',
        maxWidth: 288,
        fontSize: '16px',
        fontFamily: 'Nimbus Sans Bold',
        padding: '8px 12px',

    },
    arrow: {
        "&::before": {
            color: '#1E2D39'
        }
    }
    }))(Tooltip);


const regLineMaxWidth = window && window.innerWidth < 960 ? Math.round(window.innerWidth * (1/2)) : 350;

const useStyles = makeStyles((theme) => ({
    tableHeaders : {
        color: 'white',
        fontFamily: 'Nimbus Sans Bold',
        fontSize: '14px'
    },
    totalsRow: {
        backgroundColor: '#F4F5F6'
    },
    tRoweHeader: {
        fontFamily: 'Nimbus Sans Bold',
        fontSize: '14px',
        lineHeight: '14px',
        paddingBottom: '12px'
    },
    agreementTypeName : {
        fontFamily: 'Nimbus Sans Bold',
        fontSize: '20px',
        lineHeight: '23px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: `${regLineMaxWidth}px`,
        [theme.breakpoints.down('sm')]: {
            fontSize: 16,
        }
    },
    owners: {
        fontFamily: 'Nimbus Sans Reg',
        fontSize: '14px'
    },
    agrTotal: {
        fontFamily: 'Nimbus Sans Bold',
        fontSize: '16px'
    },
    iconBtn: {
        color: '#086EA1',
        paddingRight: 0,
        paddingBottom: 0
    },
    fundName: {
        fontFamily: 'Nimbus Sans Reg',
        fontSize: '16px'
    },
    ticker: {
        fontFamily: 'Nimbus Sans Light',
        fontSize: '12px',
        paddingLeft: '8px'
    },
    agrNum: {
        fontFamily: 'Nimbus Sans Light',
        fontSize: '14px'
    },
    agrFundTotal: {
        fontFamily: 'Nimbus Sans Reg',
        fontSize: '16px'
    },
    agrPercent: {
        fontFamily: 'Nimbus Sans Light',
        fontSize: '14px'
    },
    cumlRow: {
        fontFamily: 'Nimbus Sans Bold',
        fontSize: '16px',
        lineHeight: '20px'
    },
}))

function Row(props) {
    const { row, open: defaultOpen } = props;
    const [open, setOpen] = React.useState(defaultOpen);
    const classes = useStyles();
    const nonRasAgreementGroupByTypeTotal = calculateNonRasAgreementGroupByTypeTotal(row);

    return (
        <>
            <TableRow className={classes.totalsRow} onClick={() => setOpen(!open)} id={`asset-table-row-${row.agreementTypeId}`}>
                <TableCell id={`agr-info-container-${row.agreementTypeId}`}>
                    <Typography className={classes.tRoweHeader} id={`t-rowe-header-${row.agreementTypeId}`}>
                        <span style={{ whitespace: 'nowrap'}}>T. ROWE PRICE</span>
                    </Typography>
                    {row.agreementTypeName && row.agreementTypeName.includes('Trust')
                        ? <TRPTooltip
                            placement='top'
                            arrow
                            enterDelay={500}
                            title={
                                <span className='blocked_data'>
                                    {row.agreementTypeName} {row.registrationLines.join(' ')}
                                </span>
                            }
                        >
                            <Typography className={classes.agreementTypeName} id={`agr-type-name-${row.agreementTypeId}`}>
                                <span className='blocked_data'>
                                    {`${row.agreementTypeName.toUpperCase()}${row.registrationLines.reduce((agg, regLine) => `${agg} ${regLine}`, "").slice(0, 100)}`}
                                </span>
                            </Typography>
                        </TRPTooltip>
                        : <Typography className={classes.agreementTypeName} id={`agr-type-name-${row.agreementTypeId}`}>{formatAgreementTypeName(row)}</Typography>
                    }
                    <Typography className={`${classes.owners} blocked_data`} id={`agr-owners-${row.agreementTypeId}`}>
                        {getOwnersStringForAgreementGroupByType(row)}
                        {row.deceasedOwner && row.deceasedOwner.length ? `, ${row.deceasedOwner} (DCD)` : ''}
                        {row.powerOfAttorney && row.powerOfAttorney.length ? `, ${row.powerOfAttorney} (Agent)` : ''}
                    </Typography>
                </TableCell>
                <TableCell align="right">
                    <Typography className={classes.agrTotal} id={`agr-total-${row.agreementTypeId}`}>
                        {formatter.format(nonRasAgreementGroupByTypeTotal)}
                    </Typography>
                        <IconButton
                            aria-label="expand row"
                            className={classes.iconBtn}
                            id={`icon-btn-${row.agreementTypeId}`}
                            size="medium" onClick={() => setOpen(!open)}
                            style={{ background: 'none'}}
                            disableRipple
                            disableFocusRipple
                        >
                            {open ?
                                <ExpandLessIcon fontSize="small" style={{ border: '1px solid #086EA1', borderRadius: '5em' }} />:
                                <ExpandMoreIcon fontSize="small" style={{ border: '1px solid #086EA1', borderRadius: '5em' }} />}
                        </IconButton>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0, paddingRight: 0 }} colSpan={6}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Table>
                            <TableBody>
                                {row.agreements.map((agreement) => (
                                    <TableRow key={agreement.agreementId} id={`agreement-${agreement.agreementId}`}>
                                            <TableCell style={{ paddingLeft: 0 }} align="left" id={`fund-info-container-${agreement.agreementId}`}>
                                                <Typography display="inline" className={classes.fundName} id={`fund-name-${agreement.agreementId}`}>
                                                    {determineFundName(agreement, row.productTypeId)}
                                                </Typography>
                                                <Typography display="inline" className={classes.ticker} id={`ticker-${agreement.agreementId}`}>
                                                    { determineFundTicker(agreement, row.productTypeId) }
                                                </Typography>

                                                <Typography className={`${classes.agrNum} blocked_data`} id={`agr-num-${agreement.agreementId}`}>
                                                    {agreement.agreementNumber}{agreement.checkSum ? `-${agreement.checkSum}` : null}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right" id={`fund-amt-container-${agreement.agreementId}`}>
                                                <Typography className={classes.agrFundTotal} id={`agr-fund-total-${agreement.agreementId}`}>
                                                    {formatter.format(agreement.dedicatedAmount)}
                                                </Typography>
                                                <Typography className={classes.agrPercent} id={`agr-percent-${agreement.agreementId}`}>
                                                    {nonRasAgreementGroupByTypeTotal.toFixed() === 0.00 ? 
                                                        "0" : (agreement.dedicatedAmount / nonRasAgreementGroupByTypeTotal * 100).toFixed()}%
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
}

/* eslint-disable react/no-array-index-key */
const AssetsTable = ({ agreementsGroupByType, externalAccounts, clientDetails }) => {
    const classes= useStyles();
    // calculate total for all non-RAS internal agreements coming into the RAS program
    const nonRasIncomingAgreementsTotal = calculateNonRasAgreementGroupByTypeArrTotal(agreementsGroupByType);
    // calculate total for all external accounts coming into the RAS program
    const externalAccountsIncomingTotal = calculateExternalAccountsTotal(externalAccounts);
    // excludes existing RAS
    const totalNewFundsToRas = nonRasIncomingAgreementsTotal + externalAccountsIncomingTotal;

    // this section displays totals respective to
    return (
        <TableContainer component={Paper}>
            <Table aria-label="collapsible table" id='full-assets-table'>
                <TableHead id='assets-table-head'>
                    <TableRow style={{ backgroundColor: '#1E2D39' }} id='assets-table-header-container'>
                        <TableCell align="left" id='curr-acct-cell'>
                            <Typography className={classes.tableHeaders} id='curr-acct-header'>
                                T. ROWE PRICE ACCOUNTS
                            </Typography>
                        </TableCell>
                        <TableCell align="right" id='tot-avb-cell'>
                            <Typography className={classes.tableHeaders} id='tot-avb-header'>
                                TOTAL AVAILABLE
                            </Typography>
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody id='asset-table-body'>
                    {agreementsGroupByType.filter(agrGroupByType => agrGroupByType.agreements.length > 0).map((agrGroupByType, index) => (
                        <Row key={index} row={agrGroupByType} open={index === 0} />
                    ))}
                    {!externalAccounts.length && (
                    <TableRow style={{ borderTop: '2px solid #05C3DE' }} id='cuml-row'>
                        <TableCell align="left" style={{padding: '24px 16px'}} id='tot-adv-assets'>
                            <Typography className={classes.cumlRow} id='tot-assets-header'>
                                TOTAL ASSETS FOR ADVISORY SERVICE
                            </Typography>
                        </TableCell>
                        <TableCell align="right" style={{padding: '24px 16px'}}>
                            <Typography className={classes.cumlRow} id='tot-assets-amt'>
                                {formatter.format(totalNewFundsToRas)}
                            </Typography>
                        </TableCell>
                    </TableRow>)}
                </TableBody>
            </Table>
            {externalAccounts.length > 0 && (
                <ExternalTable
                    externalAccounts={externalAccounts}
                    clientDetails={clientDetails}
                    dedicatedTotal={totalNewFundsToRas}
                />
            )}
        </TableContainer>
    )
};

export default AssetsTable;
